// "Copy and edit" and "Edit": the request that authors a template again — the
// server's own (`GET .../templates/{id}/authoring`, one mapping, server-side) —
// as the builder's form state.
//
// THE INVERSE of `toRuleParams` (ruleParamsWire.ts) and of the request
// `templateRequest` builds (templateRequest.ts), driven by the same
// `RULE_PARAMS_WIRE` table so the two directions cannot name a term differently.
//
// NEVER A PARTIAL COPY. Every rule is sent back through the forward mapping
// right here; one that does not come out as the rule that went in — a family,
// an arm, a bucket or a consequence the builder cannot state — refuses the
// whole copy. Every term the form has no field for stays in the loaded request
// (`base`), which `templateRequest(form, base)` sends back as it was loaded.

import { KEPT_DAYS_PREFILL, type CustomChallengeBuilderInput, type StepBuilderInput } from '../form';
import type { CreateRuleRequest, CreateStepRequest, CreateTemplateRequest } from '../wire';
import { RULE_PARAMS_WIRE, ruleAuthoringRefusal, toRuleParams } from './ruleParamsWire';
import {
  ACCOUNT_SCOPE, RULE_REGISTRY, defaultRuleState, needsCalendarSource, type RuleBuilderState, type RuleScope,
} from './ruleRegistry';

type Terms = Record<string, unknown>;

/** The form, with the request it was read from: every term the form has no
 *  field for is sent back from `base`. */
export type BuilderPrefill =
  | { kind: 'ready'; form: CustomChallengeBuilderInput; base: CreateTemplateRequest }
  | { kind: 'refused'; reason: string };

export const RULE_REFUSAL = 'This template uses a rule the builder cannot edit yet.';
export const SHAPE_REFUSAL = 'This template has a phase layout the builder cannot edit yet.';
export const BILLING_REFUSAL = 'This template is billed in a way the builder cannot edit yet.';
/** The builder's three structures: instant, one phase, two phases — each ending funded. */
const MAX_STEPS = 3;

const isTerms = (value: unknown): value is Terms => typeof value === 'object' && value !== null && !Array.isArray(value);

/** "10.0000" → "10": the same number as an author types it. Exact: no float on the way. */
export function decimalText(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined) return '';
  const text = String(raw);
  return /^-?\d+\.\d+$/.test(text) ? text.replace(/\.?0+$/, '') : text;
}

/** The same authored terms: "5.00" is 5, a missing key is an unstated one, order matters. */
export function sameTerms(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((item, index) => sameTerms(item, right[index]));
  }
  if (isTerms(left) && isTerms(right)) {
    return [...new Set([...Object.keys(left), ...Object.keys(right)])].every((key) => sameTerms(left[key], right[key]));
  }
  const scalar = (value: unknown) => typeof value === 'string' || typeof value === 'number';
  if (scalar(left) && scalar(right) && String(left).trim() !== '' && String(right).trim() !== '') {
    const [a, b] = [Number(left), Number(right)];
    if (Number.isFinite(a) && Number.isFinite(b)) return a === b;
  }
  return (left ?? null) === (right ?? null);
}

/** `FlatSchedule`'s tagged arm, as the flat fields the schedule editor lays out. */
function scheduleFields(schedule: unknown): Terms {
  if (!isTerms(schedule)) return {};
  const { daily, session, calendar } = schedule;
  if (isTerms(daily) && isTerms(daily.clock)) {
    return {
      boundary: 'daily_time', time: daily.clock.time, tz: daily.clock.timezone,
      close_before_minutes: daily.close_before_minutes, blackout_after_minutes: daily.blackout_after_minutes,
    };
  }
  if (isTerms(session)) {
    const cutoff = session.latest_daily_cutoff;
    return {
      boundary: 'session_close', session_scope: session.session_scope, min_closed_minutes: session.min_closed_minutes,
      ...(isTerms(cutoff) ? { latest_cutoff_time: cutoff.time, latest_cutoff_tz: cutoff.timezone } : {}),
      close_before_minutes: session.close_before_minutes, blackout_after_minutes: session.blackout_after_minutes,
    };
  }
  if (isTerms(calendar) && isTerms(calendar.currencies)) {
    return {
      boundary: 'before_calendar_event', impacts: calendar.impacts, currencies: calendar.currencies.listed,
      close_before_minutes: calendar.before_minutes, blackout_after_minutes: calendar.after_minutes,
    };
  }
  return {};
}

/** A number and the unit it was authored in, out of a one-key tagged object. */
function taggedNumber(term: unknown, percentKey: string, absoluteKey: string): { value: string; valueMode: string } | null {
  if (!isTerms(term)) return null;
  if (term[percentKey] != null) return { value: decimalText(String(term[percentKey])), valueMode: 'percent' };
  if (term[absoluteKey] != null) return { value: decimalText(String(term[absoluteKey])), valueMode: 'absolute' };
  return null;
}

/** The pinned memberships a request states, by group and revision. */
type GroupPins = ReadonlyMap<string, string[]>;
const pinKey = (id: string, revision: number) => `${id}@${revision}`;

function scopeState(scope: CreateRuleRequest['scope'], pins: GroupPins): RuleScope | null {
  if (!scope || scope.kind === 'account') return ACCOUNT_SCOPE;
  // A named group is authored by its NAME, its membership pinned beside the
  // steps (`symbol_groups`); one with no pin would be a limit over nothing.
  if (scope.kind === 'symbol_group') {
    const members = pins.get(pinKey(scope.value.id, scope.value.revision));
    return members ? { kind: 'symbol_group', value: { ...scope.value, members } } : null;
  }
  return scope;
}

/** One authored rule as an enabled editor row — or `null` when the builder cannot state it. */
function ruleState(rule: CreateRuleRequest, pins: GroupPins): RuleBuilderState | null {
  const served: Terms = rule.params;
  const config = RULE_REGISTRY[rule.params.rule_type];
  const wire = RULE_PARAMS_WIRE[rule.params.rule_type];
  const scope = scopeState(rule.scope, pins);
  if (!config || !wire || !scope) return null;

  const params: Terms = wire.schedule ? scheduleFields(served[wire.schedule]) : {};
  if (wire.window) params[wire.window] = served[wire.window];
  for (const term of wire.terms ?? []) params[term] = served[term];
  const number = wire.money ? taggedNumber(served[wire.money], 'percent', 'amount')
    : wire.distance ? taggedNumber(served[wire.distance], 'percent_of_entry', 'price_units')
      : wire.scalar ? { value: decimalText(String(served[wire.scalar] ?? '')), valueMode: config.defaultValueMode ?? '' }
        : { value: '', valueMode: config.defaultValueMode ?? '' };
  if (!number) return null;

  const state: RuleBuilderState = {
    ...defaultRuleState(config), ...number, enabled: true, scope, params, timing: rule.timing,
    action: rule.ladder.first, repeats: rule.ladder.repeats.map(([from, action]) => [from, action]),
    warningThresholdPct: decimalText(rule.warning_threshold_pct),
  };
  // THE PROOF, per rule and at run time: forward(inverse(rule)) is the rule.
  return ruleAuthoringRefusal(state) === null && sameTerms(toRuleParams(state), rule.params) ? state : null;
}

/** The authored ladder names each limit's rule by its place in the step, as
 *  the form does: carried as it is, refused when a limit points past the rules. */
function scalingState(ladder: unknown, rules: CreateRuleRequest[]): StepBuilderInput['scalingLadder'] | null {
  if (ladder == null) return undefined;
  const authored = ladder as NonNullable<StepBuilderInput['scalingLadder']>;
  const inRange = Array.isArray(authored.rungs) && authored.rungs.every((rung) => rung.limits.every(
    (limit) => Number.isInteger(limit.rule_index) && limit.rule_index >= 0 && limit.rule_index < rules.length,
  ));
  return inRange ? authored : null;
}

function stepState(step: CreateStepRequest, pins: GroupPins): StepBuilderInput | null {
  const copied: RuleBuilderState[] = [];
  for (const rule of step.rules) {
    const state = ruleState(rule, pins);
    if (!state) return null;
    copied.push(state);
  }
  const scalingLadder = scalingState(step.scaling_ladder, step.rules);
  if (scalingLadder === null) return null;
  // Copied rules FIRST and in their own order — a scaling limit points at a
  // rule by its place — then one unused row per family the step does not use.
  const used = new Set(copied.map((rule) => rule.ruleType));
  const unused = Object.values(RULE_REGISTRY).filter((config) => !used.has(config.key)).map(defaultRuleState);
  return {
    ...(scalingLadder ? { scalingLadder } : {}),
    displayName: step.display_name,
    freshAccount: step.fresh_account,
    profitTargetPct: decimalText(step.profit_target_pct),
    profitTargetAbs: decimalText(step.profit_target_abs),
    minTradingDays: decimalText(step.min_trading_days),
    minProfitableDays: decimalText(step.min_profitable_days),
    timeLimitDays: decimalText(step.time_limit_days),
    tradingDayMode: step.trading_day_mode,
    minDailyProfit: decimalText(step.min_daily_profit),
    consistencyMode: step.consistency_mode ?? '',
    consistencyPct: decimalText(step.consistency_pct),
    consistencyBasis: step.consistency_basis ?? 'total_net_profit',
    consistencyRemedy: step.consistency_remedy ?? 'fail_step',
    consistencyWindow: step.consistency_window ?? 'account_life',
    inactivityDays: decimalText(step.inactivity_days),
    inactivityClockFrom: step.inactivity_clock_from ?? 'purchase',
    inactivityOutcome: step.inactivity_outcome ?? 'expire',
    payoutPeakPolicy: step.payout_peak_policy,
    // A day boundary is a wall-clock minute; the server may state its seconds.
    challengeDayBoundaryTime: step.challenge_day_boundary_time.replace(/^(\d{1,2}:\d{2}):00$/, '$1'),
    challengeDayBoundaryTimezone: step.challenge_day_boundary_timezone,
    rules: [...copied, ...unused],
  };
}

const PROFILE_KEY = /^standard-(netting|hedging-gross|hedging-largest-leg|hedging-offset-(\d+)pct)-(\d+)$/;

/** The inverse of the builder's execution-profile key, or `null` for a profile it cannot name. */
function executionState(request: CreateTemplateRequest): Pick<CustomChallengeBuilderInput, 'positionMode' | 'hedgeMarginMethod' | 'hedgeOffsetFactor'> | null {
  const [, profile, offsetPct, leverage] = PROFILE_KEY.exec(request.execution_profile_key) ?? [];
  if (profile === undefined || leverage !== String(request.leverage)) return null;
  if (profile === 'netting') return { positionMode: 'netting', hedgeMarginMethod: 'gross', hedgeOffsetFactor: '0.30' };
  if (offsetPct !== undefined) return { positionMode: 'hedging', hedgeMarginMethod: 'offset', hedgeOffsetFactor: (Number(offsetPct) / 100).toFixed(2) };
  return { positionMode: 'hedging', hedgeMarginMethod: profile === 'hedging-gross' ? 'gross' : 'largest_leg', hedgeOffsetFactor: '0.30' };
}

const BILLING_PERIODS = ['one_time', 'monthly'] as const;
const isBillingPeriod = (value: string): value is CustomChallengeBuilderInput['billingPeriod'] =>
  (BILLING_PERIODS as readonly string[]).includes(value);

/** The builder's form for the authoring `request`: a copy named
 *  "<name> (copy)", or, for the author editing their own, the template as it
 *  is; with the request itself as the base every unshown term is sent from. */
export function requestToBuilder(
  request: CreateTemplateRequest, purpose: 'copy' | 'edit',
): BuilderPrefill {
  const steps = request.steps;
  const last = steps.length - 1;
  const billingPeriod = request.billing_period;
  // The builder makes the LAST step the funded one, and only that one.
  if (last < 0 || last >= MAX_STEPS || steps.some((step, index) => step.is_terminal !== (index === last))) {
    return { kind: 'refused', reason: SHAPE_REFUSAL };
  }
  if (!isBillingPeriod(billingPeriod)) return { kind: 'refused', reason: BILLING_REFUSAL };
  const calendarSource = request.calendar_source ?? undefined;
  const pins: GroupPins = new Map((request.symbol_groups ?? []).map((pin) => [pinKey(pin.id, pin.revision), pin.members]));
  const formSteps: StepBuilderInput[] = [];
  for (const step of steps) {
    const state = stepState(step, pins);
    if (!state) return { kind: 'refused', reason: RULE_REFUSAL };
    // The request states one calendar source; the form keeps it on each step
    // whose rules read a calendar.
    if (calendarSource && state.rules.some(needsCalendarSource)) state.calendarSource = calendarSource;
    formSteps.push(state);
  }
  const execution = executionState(request);
  const reset = isTerms(request.reset_policy) ? request.reset_policy : {};
  return {
    kind: 'ready',
    base: request,
    form: {
      displayName: purpose === 'copy' ? `${request.display_name} (copy)` : request.display_name,
      description: request.description ?? '',
      accountSize: decimalText(request.account_size),
      currency: request.currency,
      challengePrice: decimalText(request.challenge_price),
      leverage: String(request.leverage),
      ...(execution ?? { positionMode: 'netting', hedgeMarginMethod: 'gross', hedgeOffsetFactor: '0.30' }),
      resetAllowed: reset.allowed === true,
      failedAccountEnd: request.failed_account_end.kind,
      failedAccountDays: String(request.failed_account_end.days ?? KEPT_DAYS_PREFILL),
      requiresActivation: request.requires_activation === true,
      activationPrice: decimalText(request.activation_price),
      profitSplitPct: decimalText(request.profit_split_pct),
      feeRefundPct: decimalText(request.fee_refund_pct),
      billingPeriod,
      steps: formSteps,
    },
  };
}
